import { useEffect, useState } from 'react';
import LoadingRow from '../../components/common/LoadingRow';
import lunchService from '../../api/services/lunch.service';
import { todayLunchDay } from '../../store/useLunchStore';
import useContextStore, { selectCurrentContext } from '../../store/useContextStore';
import { DAY_NAMES, DIET_TAGS } from '../../utils/constants';

/* The full week, one compact row per day. Breakfast and lunch share the row
   layout but only one meal shows at a time — toggled via the pills — and
   today's row is highlighted, same "what matters right now" idea as the parent
   app's Daily Briefing.

   Fetched here with the Lunch Menu page's own API (lunchService.getDay, one
   call per weekday), into local state rather than the page's editor store —
   so the card always shows what is saved, never a half-typed edit or the blank
   row the editor opens an empty day with. Refetched every time the dashboard
   mounts, so a save on the Lunch Menu page shows up on the way back. */
export default function LunchWeekWidget() {
  const ctx = useContextStore(selectCurrentContext);
  const [meal, setMeal] = useState('lunch');
  const [week, setWeek] = useState(null); // { Mon: { breakfast, lunch }, … } — null while loading
  const [failed, setFailed] = useState(false);
  const today = todayLunchDay();

  const schoolId = ctx?.schoolId || ctx?.id || null;
  useEffect(() => {
    setWeek(null);
    setFailed(false);
    if (!schoolId) return undefined;
    let cancelled = false;
    const days = Object.keys(DAY_NAMES);
    /* getDay answers null for a day with nothing saved (404); only a real
       failure rejects */
    Promise.all(days.map((d) => lunchService.getDay(d, schoolId)))
      .then((saved) => {
        if (cancelled) return;
        setWeek(
          Object.fromEntries(
            days.map((d, i) => [d, { breakfast: saved[i]?.breakfast || [], lunch: saved[i]?.lunch || [] }])
          )
        );
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [schoolId]);

  if (failed) {
    return <div className="hint" style={{ padding: '8px 0' }}>Couldn't load this week's menu — refresh to try again.</div>;
  }
  if (!week) {
    return <LoadingRow>Loading this week's menu…</LoadingRow>;
  }

  return (
    <>
      <div className="meal-toggle">
        <span className={`meal-toggle-btn ${meal === 'breakfast' ? 'on' : ''}`} onClick={() => setMeal('breakfast')}>
          Breakfast
        </span>
        <span className={`meal-toggle-btn ${meal === 'lunch' ? 'on' : ''}`} onClick={() => setMeal('lunch')}>
          Lunch
        </span>
      </div>
      <div>
        {Object.keys(week).map((day) => {
          /* an option saved with no name is an editor leftover, not a dish */
          const opts = week[day][meal].filter((o) => o.text.trim());
          return (
            <div key={day} className={`lw-day-block ${day === today ? 'is-today' : ''}`}>
              <span className="lw-day-label">{day === today ? 'Today' : day}</span>
              <div className="lw-day-opts">
                {opts.length ? (
                  opts.map((opt, i) => {
                    const tags = (opt.tags || [])
                      .map((id) => DIET_TAGS.find((t) => t.id === id)?.icon || '')
                      .join(' ');
                    return (
                      <div className="lw-opt-row" key={opt.id}>
                        <span className="lw-opt-num">{i + 1}</span>
                        <span className="lw-opt-text">{opt.text}</span>
                        {tags && <span className="lw-opt-tags">{tags}</span>}
                      </div>
                    );
                  })
                ) : (
                  <div className="lw-opt-row">
                    <span className="lw-opt-text" style={{ color: 'var(--muted)' }}>Nothing set yet</span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}
